import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import {
  Associate,
  AssociatePasswordChangeBody,
  AssociateReqBody,
} from '../../../types/associates';
import { AcademiaResponse } from '../../../types/common';
import { FeaturePermission } from '../../../types/permissions';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useAssociateActions = (
  whichAction: 'associateList' | 'userFeaturePermissions' | '',
  associateId?: number
) => {
  // fetching the associate list here
  const associateListHandler = useQuery({
    queryKey: ['associate-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Associate>>> => {
      const response = await getPrivateAxiosInstance('user').get('/user/list/');

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'associateList'),
  });

  // query function to fetch associate details by associate id declared here
  const getAssociateDetailsById = useQuery({
    queryKey: ['associate-details-by-id', associateId],
    queryFn: async (): Promise<AcademiaResponse<Associate>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/user/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!associateId),
  });

  // query function to fetch associate's feature permissions by associate id declared here
  const getUserFeaturePermissionsById = useQuery({
    queryKey: ['user-feature-permissions', associateId],
    queryFn: async (): Promise<AcademiaResponse<FeaturePermission>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/user/specific/feature/permissions/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(
      whichAction === 'userFeaturePermissions' && !!associateId
    ),
  });

  // create new associate mutation function declared here
  const createNewAssociateHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: AssociateReqBody;
    }): Promise<AcademiaResponse<Associate>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/user/create/',
        body
      );

      return response.data;
    },
  });

  // update existing associate information mutation function declared here
  const updateAssociateHandler = useMutation({
    mutationFn: async ({
      associateId,
      body,
    }: {
      associateId: number;
      body: AssociateReqBody;
    }) => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/${associateId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // change associate password mutation function declared here
  const changeAssociatePasswordHandler = useMutation({
    mutationFn: async ({
      associateId,
      body,
    }: {
      associateId: number;
      body: AssociatePasswordChangeBody;
    }): Promise<AcademiaResponse<Associate>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/${associateId}/partial/update/`,
        {
          user: body,
          user_profile: { languages_spoken: null },
          user_expertise: null,
          feature_permissions: null,
        }
      );

      return response.data;
    },
  });

  // toggling associate active/deactive status mutation function declared here
  const toggleAssociateActivityHandler = useMutation({
    mutationFn: async ({
      associateId,
      body,
    }: {
      associateId: number;
      body: {
        user: { is_active: boolean };
        user_profile: { languages_spoken: null };
        feature_permissions: null;
      };
    }): Promise<AcademiaResponse<Associate>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/user/${associateId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete associate by id declared here
  const deleteAssociateHandler = useMutation({
    mutationFn: ({
      associateId,
    }: {
      associateId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/user/${associateId}/delete/`),
  });

  return {
    associateListHandler,
    getAssociateDetailsById,
    getUserFeaturePermissionsById,
    createNewAssociateHandler,
    updateAssociateHandler,
    changeAssociatePasswordHandler,
    deleteAssociateHandler,
    toggleAssociateActivityHandler,
  };
};

export default useAssociateActions;
